import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import CommonIssues from "../components/site/CommonIssues";
import CostFactors from "../components/site/CostFactors";
import FAQBlock from "../components/site/FAQBlock";
import ProcessSteps from "../components/site/ProcessSteps";
import useSEO from "../hooks/useSEO";
import {
  Settings2,
  Cpu,
  ClipboardCheck,
  FileCheck2,
  Search,
  BatteryCharging,
  ArrowUpCircle,
  Lightbulb,
} from "lucide-react";

const IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/lmp91ro0_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_52_44%20PM.png";

export default function EmergencyLighting() {
  useSEO({
    title: "Emergency Lighting Ireland | Installation, Testing & Certification",
    description:
      "Emergency lighting design, installation, testing, certification and maintenance services for commercial and industrial premises across Ireland.",
    canonicalPath: "/services/emergency-lighting",
  });

  return (
    <>
      <PageHero
        eyebrow="Emergency Lighting"
        title="Emergency Lighting"
        subtitle="Emergency lighting provides essential illumination during a mains power failure, helping occupants safely locate escape routes and exits. Fire Safety Systems provides emergency lighting design, installation, testing, certification and maintenance services for commercial and industrial premises."
        image={IMG}
        imageAlt="FSS engineer testing an emergency lighting fitting over an exit sign"
        ctaPrimary={{ to: "/contact", label: "Request a Quote" }}
        ctaSecondary={{ to: "/services/testing-certification", label: "Testing & Certification" }}
      />

      <ServiceBody
        includedSubtitle="Our emergency lighting services"
        included={[
          { icon: Settings2, title: "Design", copy: "Emergency lighting design tailored to your building layout and escape routes." },
          { icon: Cpu, title: "Installation", copy: "Professional installation of emergency lighting fixtures and systems." },
          { icon: ClipboardCheck, title: "Testing", copy: "Scheduled testing to help ensure lighting operates when required." },
          { icon: FileCheck2, title: "Certification", copy: "Clear documentation issued following completed testing and servicing." },
          { icon: Search, title: "Annual inspections", copy: "Yearly inspections in line with your fire safety programme." },
          { icon: BatteryCharging, title: "Battery replacement", copy: "Replacement of degraded emergency lighting batteries." },
          { icon: ArrowUpCircle, title: "System upgrades", copy: "Upgrade older lighting to modern, energy-efficient fixtures." },
          { icon: Lightbulb, title: "Fixture replacement", copy: "Replacement of damaged or end-of-life emergency luminaires." },
        ]}
        whoFor={[
          "Commercial buildings and offices",
          "Retail, hospitality and public premises",
          "Industrial sites and warehouses",
          "Property managers coordinating multi-site testing",
        ]}
        benefits={[
          "Escape routes properly illuminated during a power failure",
          "Reliable equipment supported by routine testing and servicing",
          "Clear inspection records maintained for your fire safety file",
          "Proactive identification of failing components and batteries",
        ]}
        defaultService="Emergency Lighting"
      />

      <section className="bg-slate-50 border-y border-slate-200 fss-section">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="fss-overline">Why regular inspections matter</div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Keep your emergency lighting inspection and service records up to
              date.
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p>
              Emergency lighting only performs its critical role if the
              batteries, fittings and control gear are regularly inspected and
              tested. Faults and degradation can develop over time, particularly
              with battery-powered units.
            </p>
            <p>
              Fire Safety Systems can carry out routine inspections and provide
              appropriate service and test records so you can maintain a clear
              picture of your emergency lighting system.
            </p>
          </div>
        </div>
      </section>

      <CommonIssues
        title="Common reasons for an emergency lighting enquiry"
        items={[
          "Failed fittings identified during a test",
          "Battery faults or degraded standby performance",
          "Damaged luminaires",
          "Areas with poor or insufficient illumination",
          "Missing or incomplete test records",
          "Building alterations affecting escape routes",
          "New or amended escape routes following a refit",
          "Change of building use or occupancy",
          "Outdated equipment nearing end of life",
          "Preparation for an upcoming inspection or audit",
        ]}
      />

      <CostFactors
        title="What affects emergency lighting cost"
        intro="Emergency lighting cost varies depending on the scale of the works. We quote based on what your building actually needs."
        factors={[
          "Number of fittings to be inspected or installed",
          "Type of luminaires (self-contained vs. central battery)",
          "Building size and layout",
          "Existing wiring and cable infrastructure",
          "Access requirements (working at height, restricted areas)",
          "Battery replacement scope",
          "Any additional escape-route lighting requirements",
          "Whether the works can be completed in one visit",
        ]}
      />

      <ProcessSteps
        eyebrow="Service process"
        title="How an emergency lighting service typically works"
        steps={[
          { title: "Site Inspection", copy: "On-site walkthrough of the installed system and escape routes." },
          { title: "System Review", copy: "Review of the existing luminaires, batteries and control gear." },
          { title: "Faults & Gaps", copy: "Identification of failed fittings and any coverage gaps." },
          { title: "Recommended Work", copy: "A clear list of recommended remedial or upgrade actions." },
          { title: "Testing or Installation", copy: "Completion of the agreed testing, repair or installation work." },
          { title: "Documentation", copy: "Clear service information and support for your future maintenance." },
        ]}
        bg="slate"
      />

      <FAQBlock
        eyebrow="Emergency lighting FAQ"
        title="Frequently asked questions"
        items={[
          { q: "What is emergency lighting?", a: "Emergency lighting provides illumination during a mains power failure, supporting safe movement towards escape routes and exits." },
          { q: "Where is emergency lighting required?", a: "The requirement depends on the building type, occupancy and applicable regulations. We can advise during a site survey." },
          { q: "How is emergency lighting tested?", a: "Testing typically includes short functional tests and longer duration tests to confirm the battery back-up. We will agree an appropriate schedule with you." },
          { q: "What happens if a fitting fails?", a: "Failed fittings are reported with a recommendation to repair or replace the fitting or battery, depending on the condition." },
          { q: "Can batteries be replaced?", a: "Yes — battery replacement is a routine part of emergency lighting servicing." },
          { q: "Can FSS upgrade an existing system?", a: "Yes — we can upgrade older luminaires to modern, energy-efficient fittings where practical." },
          { q: "What affects the cost?", a: "The number and type of fittings, building layout, access requirements, battery scope and whether the work can be completed in one visit." },
          { q: "What records are provided?", a: "Clear service information covering fittings tested, faults identified and any recommended remedial actions." },
        ]}
      />

      <CTABanner
        title="Book an Emergency Lighting Inspection"
        subtitle="Contact FSS to arrange an inspection, service or quotation."
        primary={{ to: "/contact", label: "Book a Service" }}
        secondary={{ to: "/contact", label: "Request a Quote" }}
      />
    </>
  );
}
